import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

type Language = "ko" | "en" | "zh" | "ja";

const LANG_LABELS: Record<Language, string> = {
  ko: "🇰🇷 KO",
  en: "🇺🇸 EN",
  zh: "🇨🇳 ZH",
  ja: "🇯🇵 JA",
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** OG 이미지 URL — 없으면 플레이스홀더 표시 */
  imageUrl?: string;
  title?: string;
  description?: string;
  siteName?: string;
  pageUrl?: string;
  defaultLang?: Language;
  onLangChange?: (lang: Language) => void;
};

/**
 * OG(Open Graph) 미리보기 모달.
 * 카카오톡/페이스북 링크 카드 스타일 + 언어 전환 토글.
 */
export function OgPreviewModal({
  open,
  onClose,
  imageUrl,
  title = "봉은사",
  description,
  siteName = "bongeunsa.app",
  pageUrl,
  defaultLang = "ko",
  onLangChange,
}: Props) {
  const [lang, setLang] = useState<Language>(defaultLang);
  const [langDropdown, setLangDropdown] = useState(false);

  function switchLang(l: Language) {
    setLang(l);
    setLangDropdown(false);
    onLangChange?.(l);
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[90vw] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-800">OG 미리보기</span>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <button
                    onClick={() => setLangDropdown((v) => !v)}
                    className="flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    {LANG_LABELS[lang]}
                    <span className="text-gray-400">{langDropdown ? "▲" : "▼"}</span>
                  </button>
                  <AnimatePresence>
                    {langDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="absolute right-0 top-full z-10 mt-1 w-28 overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5"
                      >
                        {(Object.keys(LANG_LABELS) as Language[]).map((l) => (
                          <button
                            key={l}
                            onClick={() => switchLang(l)}
                            className={`flex w-full items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 ${
                              l === lang ? "bg-gray-50 font-semibold" : "text-gray-700"
                            }`}
                          >
                            {LANG_LABELS[l]}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-5">
              {/* Kakao-style card */}
              <div className="mb-3">
                <p className="mb-1 text-xs font-medium text-gray-400">카카오톡</p>
                <KakaoCard
                  imageUrl={imageUrl}
                  title={title}
                  description={description}
                  siteName={siteName}
                  url={pageUrl}
                />
              </div>

              {/* Facebook-style card */}
              <div>
                <p className="mb-1 text-xs font-medium text-gray-400">Facebook / 일반</p>
                <FacebookCard
                  imageUrl={imageUrl}
                  title={title}
                  description={description}
                  siteName={siteName}
                />
              </div>

              {/* URL */}
              {pageUrl && (
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
                  <span className="text-xs text-gray-400">URL</span>
                  <span className="flex-1 truncate text-xs text-gray-600">{pageUrl}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(pageUrl).catch(() => {})}
                    className="text-xs text-brand-500 hover:underline"
                  >
                    복사
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function KakaoCard({
  imageUrl,
  title,
  description,
  siteName,
  url,
}: {
  imageUrl?: string;
  title: string;
  description?: string;
  siteName: string;
  url?: string;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-[#F9F9F9]">
      {imageUrl ? (
        <img src={imageUrl} alt={title} className="h-40 w-full object-cover" />
      ) : (
        <div className="flex h-40 w-full items-center justify-center bg-brand-50">
          <p className="text-xs text-brand-300">OG 이미지</p>
        </div>
      )}
      <div className="px-3 py-2.5">
        <p className="text-xs font-bold text-gray-900 line-clamp-2">{title}</p>
        {description && (
          <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">{description}</p>
        )}
        <p className="mt-1 text-[10px] uppercase text-gray-400">{siteName}</p>
      </div>
      {url && (
        <div className="border-t border-gray-100 px-3 py-2">
          <p className="truncate text-[10px] text-gray-400">{url}</p>
        </div>
      )}
    </div>
  );
}

function FacebookCard({
  imageUrl,
  title,
  description,
  siteName,
}: {
  imageUrl?: string;
  title: string;
  description?: string;
  siteName: string;
}) {
  return (
    <div className="flex overflow-hidden rounded-xl border border-gray-200 bg-white">
      {imageUrl ? (
        <img src={imageUrl} alt={title} className="h-24 w-32 shrink-0 object-cover" />
      ) : (
        <div className="flex h-24 w-32 shrink-0 items-center justify-center bg-brand-50">
          <p className="text-[10px] text-brand-300">OG</p>
        </div>
      )}
      <div className="flex flex-col justify-center px-3 py-2 min-w-0">
        <p className="text-[10px] uppercase text-gray-400">{siteName}</p>
        <p className="mt-0.5 text-xs font-semibold text-gray-900 line-clamp-2">{title}</p>
        {description && (
          <p className="mt-0.5 text-[10px] text-gray-500 line-clamp-2">{description}</p>
        )}
      </div>
    </div>
  );
}
